import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';

// Lazy load pages for code splitting
const Login = lazy(() => import('./pages/Login'));
const LinksIndexPage = lazy(() => import('./pages/LinksIndexPage'));
const CreatePodcastPage = lazy(() => import('./pages/CreatePodcastPage'));
const EditPodcastPage = lazy(() => import('./pages/EditPodcastPage'));
const SelectPodcastTemplatePage = lazy(() => import('./pages/SelectPodcastTemplatePage'));
const SelectBioTemplatePage = lazy(() => import('./pages/SelectBioTemplatePage'));
const CreateBioPage = lazy(() => import('./pages/CreateBioPage'));
const EditBioPage = lazy(() => import('./pages/EditBioPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const ReportPage = lazy(() => import('./pages/ReportPage'));
const ReportManagementPage = lazy(() => import('./pages/ReportManagementPage').then(module => ({ default: module.ReportManagementPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(module => ({ default: module.AdminPage })));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading, sessionToken } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    // Redirect to login page with the current location as redirect parameter if not authenticated
    if (!isAuthenticated || !sessionToken) {
        const redirectPath = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/dashboard/login?redirect=${redirectPath}`} replace />;
    }

    return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    // If already authenticated, check for redirect parameter
    if (isAuthenticated) {
        const params = new URLSearchParams(location.search);
        const redirectTo = params.get('redirect');
        const destination = redirectTo ? decodeURIComponent(redirectTo) : '/dashboard/links';
        return <Navigate to={destination} replace />;
    }

    return <>{children}</>;
};

const LoadingFallback = () => (
    <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
    </div>
);

const AppRoutes: React.FC = () => {
    return (
        <Router>
            <Suspense fallback={<LoadingFallback />}>
                <Routes>
                    <Route
                        path="/dashboard"
                        element={<Navigate to="/dashboard/login" replace />}
                    />
                    <Route
                        path="/dashboard/login"
                        element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/dashboard/links"
                        element={
                            <ProtectedRoute>
                                <LinksIndexPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/podcasts"
                        element={<Navigate to="/dashboard/links" replace />}
                    />
                    <Route
                        path="/dashboard/podcasts/select-template"
                        element={
                            <ProtectedRoute>
                                <SelectPodcastTemplatePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/podcasts/new"
                        element={
                            <ProtectedRoute>
                                <CreatePodcastPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/podcasts/:id/edit"
                        element={
                            <ProtectedRoute>
                                <EditPodcastPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/bio/select-template"
                        element={
                            <ProtectedRoute>
                                <SelectBioTemplatePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/bio/new"
                        element={
                            <ProtectedRoute>
                                <CreateBioPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/bio/:id/edit"
                        element={
                            <ProtectedRoute>
                                <EditBioPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/analytics/:linkId"
                        element={
                            <ProtectedRoute>
                                <AnalyticsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/report"
                        element={
                            <ProtectedRoute>
                                <ReportPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/reports"
                        element={
                            <ProtectedRoute>
                                <ReportManagementPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/admin"
                        element={
                            <ProtectedRoute>
                                <AdminPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Suspense>
        </Router>
    );
};

const App: React.FC = () => {
    return (
        <AuthProvider>
            <div className="App">
                <AppRoutes />
            </div>
        </AuthProvider>
    );
};

export default App;
